import * as Sentry from '@sentry/react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
	MessengerMessageType,
	MessengerQueryKeys,
	PER_PAGE_SIZE_LIMIT,
} from '~components/business-owner/messenger/constants';
import { groupMessagesByDay } from '~components/business-owner/messenger/utils';

import {
	fetchConversationMessages,
	FetchConversationMessagesResponse,
} from '~api/business-owner/messenger';
import { filterDeletedMessages } from '~api/business-owner/queries/messenger/recentlyDeletedMessages';
import { ApiError } from '~types/api';
import { callErrorToast } from '~utils/toasts';

interface UseConversationMessagesParams {
	customerId: string | null;
	perPage?: number;
	page?: number;
	messageType?: MessengerMessageType;
	orderId?: string;
}

export function getConversationMessagesQueryKey({
	customerId,
	messageType,
	orderId,
}: Pick<UseConversationMessagesParams, 'customerId' | 'messageType' | 'orderId'>): Array<
	string | null
> {
	return [
		MessengerQueryKeys.CUSTOMER_MESSAGES,
		customerId,
		messageType ?? null,
		orderId ?? null,
	];
}

export const useInvalidateConversationMessages = () => {
	const queryClient = useQueryClient();

	return () => queryClient.invalidateQueries([MessengerQueryKeys.CUSTOMER_MESSAGES]);
};

export const useConversationMessages = ({
	customerId,
	perPage,
	messageType,
	orderId,
}: UseConversationMessagesParams) => {
	const {
		data,
		error,
		isFetched,
		isInitialLoading,
		refetch,
		fetchNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery<unknown, ApiError, FetchConversationMessagesResponse>({
		queryKey: getConversationMessagesQueryKey({
			customerId,
			messageType,
			orderId,
		}),
		queryFn: async ({ pageParam = 1 }) => {
			const { data } = await fetchConversationMessages(customerId as string, {
				page: pageParam,
				perPage: perPage ?? PER_PAGE_SIZE_LIMIT,
				messageType,
				orderId,
			});

			return data ?? {};
		},
		enabled: !!customerId,
		getNextPageParam: (lastPage: FetchConversationMessagesResponse) => {
			return lastPage?.meta?.hasNextPage
				? lastPage?.meta?.currentPage + 1
				: undefined;
		},
		onError: (error) => {
			Sentry.captureException(error);
			callErrorToast({
				content: 'An error occurred while loading customer messages',
			});
		},
	});

	const conversationMessages = useMemo(
		() =>
			// A delete the server has accepted can still come back from a search
			// index that has not caught up yet, so anything just deleted is held
			// back rather than flickering back into the thread.
			filterDeletedMessages(
				customerId,
				data?.pages?.flatMap((page) => page.records.map((message) => message)) ??
					[],
			),
		[data, customerId],
	);

	const groupedMessages = useMemo(
		() => groupMessagesByDay(conversationMessages),
		[conversationMessages],
	);

	const totalCount = data?.pages?.[0]?.meta?.total ?? 0;
	const currentPage = data?.pages?.[0]?.meta?.currentPage ?? 0;
	const hasNextPage = data?.pages?.[0]?.meta?.hasNextPage ?? false;

	return {
		conversationMessages,
		groupedMessages,
		conversationMessagesTotalCount: totalCount,
		conversationMessagesCurrentPage: currentPage,
		isConversationMessagesHasNextPage: hasNextPage,
		isConversationMessagesLoading: isInitialLoading || isFetchingNextPage,
		isConversationMessagesFetched: isFetched,
		conversationMessagesError: error,
		refetchConversationMessages: refetch,
		fetchNextConversationMessagesPage: fetchNextPage,
	};
};
